import { useMemo, useState } from 'react';
import { PauseCircle, PlayCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
   useCapabilityBindings,
   useUpdateCapabilityBinding,
   type BindingAction,
} from '@/hooks/use-capability-acls';

const LOG_PREFIX = '[CapabilityBindingsPanel]';

export interface CapabilityBindingsPanelProps {
   appKey: string;
   capabilityName: string;
   disabled?: boolean;
}

const PAGE_SIZE = 10;

export function CapabilityBindingsPanel({
   appKey,
   capabilityName,
   disabled,
}: CapabilityBindingsPanelProps) {
   const [page, setPage] = useState(1);
   const listQuery = useCapabilityBindings({
      appKey,
      capabilityName,
      page,
      pageSize: PAGE_SIZE,
   });
   const updateMutation = useUpdateCapabilityBinding({ appKey, capabilityName });

   const totalPages = useMemo(() => {
      const total = listQuery.data?.total ?? 0;
      return Math.max(1, Math.ceil(total / PAGE_SIZE));
   }, [listQuery.data?.total]);

   async function act(bindingID: string, action: BindingAction) {
      try {
         await updateMutation.mutateAsync({ bindingID, action });
         toast.success(`Binding ${action}d`);
      } catch (err) {
         console.error(`${LOG_PREFIX}.act - failed`, { bindingID, action, err });
         toast.error('Failed to update binding', {
            description: err instanceof Error ? err.message : 'Unknown error',
         });
      }
   }

   const items = listQuery.data?.items ?? [];

   return (
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-card p-6 shadow-sm dark:border-slate-700">
         <div className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
               Worker bindings
            </h3>
            <p className="text-sm text-muted-foreground">
               Each binding authorises a worker client to subscribe to this capability's
               invocation subject. Pause to temporarily stop a worker without deleting it,
               revoke to permanently sever the binding.
            </p>
         </div>

         <div className="rounded-md border border-slate-200 dark:border-slate-700">
            {listQuery.isLoading ? (
               <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            ) : listQuery.isError ? (
               <div className="p-4 text-sm text-destructive">
                  Failed to load bindings: {(listQuery.error as Error).message}
               </div>
            ) : items.length === 0 ? (
               <div className="p-4 text-sm text-muted-foreground">No worker bindings yet.</div>
            ) : (
               <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                  {items.map((b) => (
                     <li
                        key={b.id}
                        className="flex items-center justify-between gap-3 px-4 py-3"
                     >
                        <div className="flex min-w-0 flex-col">
                           <span className="truncate font-mono text-sm text-slate-900 dark:text-slate-100">
                              {b.worker_oauth_client_id}
                           </span>
                           <span className="text-xs text-muted-foreground">
                              Consumer org {b.consumer_org_id.slice(0, 8)}… · Created{' '}
                              {new Date(b.created).toLocaleString()}
                           </span>
                        </div>
                        <div className="flex items-center gap-2">
                           <Badge
                              variant={
                                 b.status === 'active'
                                    ? 'default'
                                    : b.status === 'paused'
                                      ? 'secondary'
                                      : 'destructive'
                              }
                              className="text-xs capitalize"
                           >
                              {b.status}
                           </Badge>
                           {b.status === 'active' && (
                              <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 disabled={disabled || updateMutation.isPending}
                                 onClick={() => void act(b.id, 'pause')}
                              >
                                 <PauseCircle size={14} className="mr-1" /> Pause
                              </Button>
                           )}
                           {b.status === 'paused' && (
                              <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 disabled={disabled || updateMutation.isPending}
                                 onClick={() => void act(b.id, 'resume')}
                              >
                                 <PlayCircle size={14} className="mr-1" /> Resume
                              </Button>
                           )}
                           {b.status !== 'revoked' && (
                              <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                 disabled={disabled || updateMutation.isPending}
                                 onClick={() => void act(b.id, 'revoke')}
                              >
                                 <Trash2 size={14} className="mr-1" /> Revoke
                              </Button>
                           )}
                        </div>
                     </li>
                  ))}
               </ul>
            )}
         </div>

         {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
               <span>
                  Page {page} of {totalPages}
               </span>
               <div className="flex items-center gap-2">
                  <Button
                     type="button"
                     variant="outline"
                     size="sm"
                     disabled={page <= 1}
                     onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                     Previous
                  </Button>
                  <Button
                     type="button"
                     variant="outline"
                     size="sm"
                     disabled={page >= totalPages}
                     onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                     Next
                  </Button>
               </div>
            </div>
         )}
      </div>
   );
}
